<template>
  <v-container class="modern-detail-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <v-btn icon @click="$router.go(-1)" class="mr-4 grey lighten-4 shadow-sm">
        <v-icon color="primary" small>fas fa-chevron-left</v-icon>
      </v-btn>
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-exchange-alt</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Traspaso entre Bodegas</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Mueve stock de una bodega a otra sin cambiar el inventario total</div>
        </div>
      </div>
    </v-row>

    <ValidationObserver v-slot="{handleSubmit}">
      <v-form @submit.prevent="handleSubmit(save)" ref="formulario">
        <v-card outlined class="modern-card shadow-sm pa-6 mb-8 mt-2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Origen y destino</div>
          <v-row>
            <v-col cols="12" md="4">
              <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Bodega origen</div>
              <ValidationProvider name="Bodega origen" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="fromStore"
                  :items="stores"
                  :loading="loading"
                  :disabled="loading"
                  item-text="name"
                  item-value="id"
                  placeholder="Seleccione bodega origen"
                  outlined
                  dense
                  class="modern-input"
                  :error-messages="errors"
                  prepend-inner-icon="fas fa-warehouse"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="4">
              <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Bodega destino</div>
              <ValidationProvider name="Bodega destino" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="toStore"
                  :items="toStores"
                  :loading="loading"
                  :disabled="loading || !fromStore"
                  item-text="name"
                  item-value="id"
                  placeholder="Seleccione bodega destino"
                  outlined
                  dense
                  class="modern-input"
                  :error-messages="errors"
                  prepend-inner-icon="fas fa-warehouse"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="4">
              <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Referencia / Glosa</div>
              <v-text-field
                v-model="notes"
                placeholder="Ej: Reposición planta, traslado producción..."
                outlined
                dense
                class="modern-input"
                hide-details
                prepend-inner-icon="fas fa-pen"
              />
            </v-col>
          </v-row>
        </v-card>

        <v-card outlined class="modern-card shadow-sm overflow-hidden mb-8">
          <v-sheet color="grey lighten-4" class="px-6 py-4 d-flex align-center border-bottom">
            <v-icon color="primary" x-small class="mr-2">fas fa-boxes-stacked</v-icon>
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Artículos a traspasar</span>
            <v-spacer></v-spacer>
            <div style="width: 400px">
              <add-article :disabled="!fromStore || !toStore" @add="addArticle" />
            </div>
          </v-sheet>

          <div v-if="articles.length > 0">
            <v-simple-table class="modern-table">
              <thead>
                <tr>
                  <th class="table-header-2">Artículo</th>
                  <th class="table-header-2 text-center">Unidad</th>
                  <th class="table-header-2 text-center">Stock origen</th>
                  <th class="table-header-2 text-center">Stock destino</th>
                  <th class="table-header-2 text-center" style="width: 140px">Cantidad</th>
                  <th class="table-header-2 text-center" style="width: 80px">Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in articles" :key="item.id">
                  <td>
                    <div class="d-flex flex-column">
                      <span class="caption font-weight-black primary--text lh-1 mb-1">[{{item.sku}}]</span>
                      <span class="body-2 font-weight-bold grey--text text--darken-4 text-truncate" style="max-width: 250px">{{item.name}}</span>
                    </div>
                  </td>
                  <td class="text-center font-weight-bold grey--text uppercase caption">{{item.unit_name}}</td>
                  <td class="text-center">
                    <span class="caption font-weight-black" :class="item.from_stock > 0 ? 'primary--text' : 'error--text'">
                      {{item.from_stock | number('0,0.00')}}
                    </span>
                  </td>
                  <td class="text-center caption font-weight-bold grey--text">
                    {{item.to_stock | number('0,0.00')}}
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="item.qty"
                      type="number"
                      outlined dense hide-details
                      class="modern-input-small text-center"
                      :max="item.from_stock"
                      min="0.01"
                      step="0.01"
                    />
                  </td>
                  <td class="text-center">
                    <v-btn icon x-small color="error" class="grey lighten-4" @click.stop="removeArticle(item)">
                      <v-icon x-small>fas fa-trash</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else class="pa-12 text-center bg-lightgrey">
            <v-icon size="64" color="grey lighten-3" class="mb-4">fas fa-exchange-alt</v-icon>
            <div class="text-h6 grey--text font-weight-bold mb-1">No hay artículos seleccionados</div>
            <div class="caption grey--text uppercase tracking-widest">Seleccione origen y destino, luego agregue artículos</div>
          </div>
        </v-card>

        <v-row v-if="articles.length > 0" class="mt-8">
          <v-col class="text-center">
            <v-btn
              color="primary"
              large
              elevation="6"
              type="submit"
              class="px-10 font-weight-black"
              :loading="loading"
            >
              <v-icon left>fas fa-save</v-icon>
              PROCESAR TRASPASO
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { fetchStores, createStoreTransfer } from '@/api/stocks'
import AddArticle from '@/components/stocks/AddArticle'
import notifications from '@/utils/notifications'

export default {
  name: 'CreateStoreTransfer',
  components: { AddArticle },
  data() {
    return {
      loading: false,
      stores: [],
      fromStore: '',
      toStore: '',
      notes: '',
      articles: [],
    }
  },
  computed: {
    toStores() {
      return this.stores.filter(store => store.id !== this.fromStore)
    }
  },
  methods: {
    stockQty(item, storeId) {
      const stock = (item.stocks || []).find(s => s.store == storeId)
      return stock ? parseFloat(stock.qty || 0) : 0
    },
    addArticle(item) {
      const alreadyExist = this.articles.filter(a => a.id == item.id).length
      if (alreadyExist) return
      item.from_stock = this.stockQty(item, this.fromStore)
      item.to_stock = this.stockQty(item, this.toStore)
      item.qty = 0
      this.articles.push(item)
    },
    removeArticle(item) {
      this.articles = this.articles.filter(i => i.id != item.id)
    },
    refreshStocks() {
      this.articles.forEach(item => {
        item.from_stock = this.stockQty(item, this.fromStore)
        item.to_stock = this.stockQty(item, this.toStore)
      })
    },
    async save() {
      if (this.fromStore === this.toStore) {
        notifications.showError('La bodega origen y destino deben ser distintas')
        return
      }
      if (!this.articles.length) {
        notifications.showError('Debe indicar al menos un artículo')
        return
      }
      let invalid = false
      this.articles.forEach(item => {
        const qty = parseFloat(item.qty)
        if (!qty || qty <= 0) {
          notifications.showError(`[${item.sku}] ${item.name}: la cantidad debe ser mayor a 0`)
          invalid = true
        } else if (qty > parseFloat(item.from_stock)) {
          notifications.showError(`[${item.sku}] ${item.name} no tiene stock suficiente en origen`)
          invalid = true
        }
      })
      if (invalid) return

      this.loading = true
      try {
        await createStoreTransfer({
          from_store: this.fromStore,
          to_store: this.toStore,
          notes: this.notes,
          lines: this.articles.map(item => ({
            article: item.id,
            qty: parseFloat(item.qty)
          }))
        })
        notifications.showSuccess('Traspaso registrado correctamente')
        this.$router.replace({ name: 'StoreTransfers' })
      } catch (e) {
        notifications.showError(e.message || 'No se pudo registrar el traspaso')
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    fromStore() {
      if (this.fromStore === this.toStore) this.toStore = ''
      this.articles = []
    },
    toStore() {
      this.refreshStocks()
    }
  },
  async created() {
    this.stores = await fetchStores()
  }
}
</script>

<style scoped>
.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }

.modern-input-small >>> .v-input__control fieldset { border-radius: 4px !important; border-color: #e0e4e8 !important; }
.modern-input-small >>> input { font-size: 0.9rem !important; font-weight: 700 !important; color: #1a1f2e !important; }

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 52px !important; text-transform: uppercase; font-size: 0.65rem !important; letter-spacing: 1.5px; font-weight: 900 !important; background-color: #f8fbfc !important; color: #607d8b !important; }
.modern-table >>> td { padding: 12px 16px !important; border-bottom: 1px solid #f0f3f5 !important; }

.v-avatar.border-white-sm { border: 2px solid white !important; }
</style>
